/**
 * Sprint 6 · Semana 3.3 — Onboarding Tour del Wizard
 *
 * Tour guiado de 5 pasos que se muestra la primera vez que un usuario
 * entra al ScenarioWizard. Resalta:
 *   1. Health Score
 *   2. Modo Guiado ↔ Power
 *   3. Guardar como plantilla
 *   4. Copilot IA
 *   5. Ejecutar Análisis
 *
 * Persistencia: localStorage.stratika_wizard_tour_completed = '1'
 * Reset: window.dispatchEvent(new Event('reset-wizard-tour'))
 */
import { useEffect, useState, useLayoutEffect, useRef } from 'react';
import { Button } from './ui/button';
import { X, ChevronRight, ChevronLeft, Sparkles, Trophy } from 'lucide-react';

const TOUR_KEY = 'stratika_wizard_tour_completed_v1';

const TOUR_STEPS = [
  {
    id: 'welcome',
    target: null,
    title: 'Bienvenido al Wizard de Escenarios',
    description: 'Te doy un tour rápido de las 4 mejoras que hicimos para que configurar un escenario sea 10x más rápido. Toma 30 segundos.',
    placement: 'center',
    icon: Sparkles,
  },
  {
    id: 'health-score',
    target: '[data-testid="wizard-health-score"]',
    title: 'Health Score en vivo',
    description: 'Este indicador (0-100) evalúa la coherencia de tu escenario en tiempo real. Si baja a rojo, hay un problema que debes revisar antes de ejecutar el análisis.',
    placement: 'bottom',
  },
  {
    id: 'mode-toggle',
    target: '[data-testid="wizard-mode-toggle"]',
    title: 'Modo Guiado ↔ Power',
    description: 'En Modo Guiado (default) ocultamos los campos avanzados para que veas sólo lo esencial. Cuando necesites TIIE variable, curvas JSON o sensibilidades, cambia a Modo Power.',
    placement: 'bottom',
  },
  {
    id: 'save-template',
    target: '[data-testid="save-template-btn"]',
    title: 'Guardar como plantilla',
    description: 'Cuando tengas un escenario bien calibrado, guárdalo como plantilla. Podrás aplicarla a cualquier proyecto futuro con un click y poblar todos los inputs, tipologías y waterfall.',
    placement: 'bottom',
  },
  {
    id: 'copilot',
    target: '[data-testid="wizard-copilot-toggle-open"]',
    title: 'Copilot IA de Configuración',
    description: 'Platícale tu escenario en lenguaje natural ("puente de $30M al 65%, plazo 24 meses, tasa 12%") y él traduce todo a las variables correctas. Aprueba los cambios con un click.',
    placement: 'left',
  },
];

function useTargetRect(selector) {
  const [rect, setRect] = useState(null);

  useLayoutEffect(() => {
    if (!selector) { setRect(null); return; }
    const compute = () => {
      const el = document.querySelector(selector);
      if (!el) { setRect(null); return; }
      const r = el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    };
    compute();
    // Recompute on resize/scroll/mutations
    window.addEventListener('resize', compute);
    window.addEventListener('scroll', compute, true);
    const mo = new MutationObserver(compute);
    mo.observe(document.body, { childList: true, subtree: true, attributes: true });
    const t = setInterval(compute, 300);  // fallback safety
    return () => {
      window.removeEventListener('resize', compute);
      window.removeEventListener('scroll', compute, true);
      mo.disconnect();
      clearInterval(t);
    };
  }, [selector]);

  return rect;
}


function computeTooltipPosition(rect, placement, tooltipSize = { w: 340, h: 200 }) {
  if (!rect) {
    // Center on viewport
    return {
      top: `calc(50vh - ${tooltipSize.h / 2}px)`,
      left: `calc(50vw - ${tooltipSize.w / 2}px)`,
    };
  }
  const gap = 12;
  const vh = window.innerHeight;
  const vw = window.innerWidth;

  let top, left;
  switch (placement) {
    case 'bottom':
      top = rect.top + rect.height + gap;
      left = Math.max(16, Math.min(vw - tooltipSize.w - 16, rect.left + rect.width / 2 - tooltipSize.w / 2));
      break;
    case 'top':
      top = rect.top - tooltipSize.h - gap;
      left = Math.max(16, Math.min(vw - tooltipSize.w - 16, rect.left + rect.width / 2 - tooltipSize.w / 2));
      break;
    case 'left':
      top = Math.max(16, Math.min(vh - tooltipSize.h - 16, rect.top + rect.height / 2 - tooltipSize.h / 2));
      left = rect.left - tooltipSize.w - gap;
      break;
    case 'right':
      top = Math.max(16, Math.min(vh - tooltipSize.h - 16, rect.top + rect.height / 2 - tooltipSize.h / 2));
      left = rect.left + rect.width + gap;
      break;
    default:
      top = vh / 2 - tooltipSize.h / 2;
      left = vw / 2 - tooltipSize.w / 2;
  }

  // Ajustar si se sale de viewport
  if (top < 16) top = 16;
  if (left < 16) left = 16;
  if (top + tooltipSize.h > vh - 16) top = vh - tooltipSize.h - 16;
  if (left + tooltipSize.w > vw - 16) left = vw - tooltipSize.w - 16;

  return { top: `${top}px`, left: `${left}px` };
}


export function WizardTour({ forceOpen = false }) {
  const [active, setActive] = useState(false);
  const [stepIdx, setStepIdx] = useState(0);

  // Mostrar la primera vez o cuando se solicite explícitamente
  useEffect(() => {
    const completed = typeof window !== 'undefined' && localStorage.getItem(TOUR_KEY) === '1';
    if (forceOpen || !completed) {
      // Pequeño delay para dar tiempo a que el DOM del wizard renderice
      const t = setTimeout(() => setActive(true), 600);
      return () => clearTimeout(t);
    }
  }, [forceOpen]);

  // Permitir reset externo
  useEffect(() => {
    const handler = () => {
      localStorage.removeItem(TOUR_KEY);
      setStepIdx(0);
      setActive(true);
    };
    window.addEventListener('reset-wizard-tour', handler);
    return () => window.removeEventListener('reset-wizard-tour', handler);
  }, []);

  const step = TOUR_STEPS[stepIdx];
  const rect = useTargetRect(step?.target);
  const isLast = stepIdx === TOUR_STEPS.length - 1;

  const complete = () => {
    localStorage.setItem(TOUR_KEY, '1');
    setActive(false);
    setStepIdx(0);
  };

  if (!active || !step) return null;

  const tooltipPos = computeTooltipPosition(rect, step.placement);
  const Icon = step.icon;

  return (
    <div
      className="fixed inset-0"
      style={{ zIndex: 9999, pointerEvents: 'none' }}
      data-testid="wizard-tour-overlay"
    >
      {/* Backdrop oscuro con recorte (spotlight) sobre el target */}
      {rect ? (
        <svg
          className="absolute inset-0"
          width="100%" height="100%"
          style={{ pointerEvents: 'auto' }}
          onClick={complete}
        >
          <defs>
            <mask id="tour-mask">
              <rect width="100%" height="100%" fill="white" />
              <rect
                x={rect.left - 6}
                y={rect.top - 6}
                width={rect.width + 12}
                height={rect.height + 12}
                rx="10"
                fill="black"
              />
            </mask>
          </defs>
          <rect width="100%" height="100%" fill="rgba(15, 23, 42, 0.75)" mask="url(#tour-mask)" />
          {/* Ring decorativo */}
          <rect
            x={rect.left - 6}
            y={rect.top - 6}
            width={rect.width + 12}
            height={rect.height + 12}
            rx="10"
            fill="none"
            stroke="#818CF8"
            strokeWidth="2"
            style={{
              filter: 'drop-shadow(0 0 12px rgba(129, 140, 248, 0.6))',
            }}
          />
        </svg>
      ) : (
        <div
          className="absolute inset-0"
          style={{ background: 'rgba(15, 23, 42, 0.75)', pointerEvents: 'auto' }}
          onClick={complete}
        />
      )}

      {/* Tooltip / Card */}
      <div
        className="absolute rounded-2xl shadow-2xl overflow-hidden"
        style={{
          ...tooltipPos,
          width: 340,
          background: 'white',
          border: '1px solid rgba(99, 102, 241, 0.2)',
          pointerEvents: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-labelledby="wizard-tour-title"
        aria-describedby="wizard-tour-desc"
        data-testid="wizard-tour-card"
      >
        {/* Header */}
        <div
          className="px-4 pt-4 pb-3"
          style={{ background: 'linear-gradient(135deg, #EEF2FF, #F5F3FF)' }}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-2">
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, #6366F1, #8B5CF6)' }}
              >
                {Icon ? <Icon className="h-4 w-4 text-white" /> : (
                  <span className="text-xs font-bold text-white">{stepIdx + 1}</span>
                )}
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: '#5B21B6' }}>
                  Paso {stepIdx + 1} de {TOUR_STEPS.length}
                </div>
                <div className="text-sm font-display font-semibold" id="wizard-tour-title" style={{ color: '#3730A3' }}>
                  {step.title}
                </div>
              </div>
            </div>
            <button
              onClick={complete}
              className="w-6 h-6 rounded-md hover:bg-white/60 flex items-center justify-center shrink-0"
              data-testid="wizard-tour-skip"
              title="Cerrar tour"
            >
              <X className="h-3.5 w-3.5" style={{ color: '#5B21B6' }} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="px-4 py-3">
          <p className="text-xs leading-relaxed" id="wizard-tour-desc" style={{ color: '#475569' }}>
            {step.description}
          </p>
        </div>

        {/* Progress dots + Nav */}
        <div className="px-4 pb-3 flex items-center justify-between">
          <div className="flex items-center gap-1">
            {TOUR_STEPS.map((_, i) => (
              <div
                key={i}
                className="h-1.5 rounded-full transition-all"
                style={{
                  width: i === stepIdx ? 16 : 6,
                  background: i === stepIdx ? '#6366F1' : (i < stepIdx ? '#C7D2FE' : '#E2E8F0'),
                }}
              />
            ))}
          </div>
          <div className="flex items-center gap-1.5">
            {stepIdx > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setStepIdx(stepIdx - 1)}
                className="h-7 text-xs px-2"
                data-testid="wizard-tour-prev"
              >
                <ChevronLeft className="h-3 w-3" /> Anterior
              </Button>
            )}
            {isLast ? (
              <Button
                size="sm"
                onClick={complete}
                className="h-7 text-xs px-3"
                style={{ background: 'linear-gradient(135deg, #6366F1, #8B5CF6)' }}
                data-testid="wizard-tour-finish"
              >
                <Trophy className="h-3 w-3 mr-1" /> Empezar
              </Button>
            ) : (
              <Button
                size="sm"
                onClick={() => setStepIdx(stepIdx + 1)}
                className="h-7 text-xs px-3"
                style={{ background: '#6366F1' }}
                data-testid="wizard-tour-next"
              >
                Siguiente <ChevronRight className="h-3 w-3 ml-1" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/** Helper para relanzar el tour desde otro componente */
export function resetWizardTour() {
  window.dispatchEvent(new Event('reset-wizard-tour'));
}
